import { useState, useEffect } from "react";
import * as Styled from "./RollAdsModalCard.styled";
import { Col, Modal } from "react-bootstrap";
import Select from "react-select";
import deleteIcon from "../../../../../assets/images/delete.svg";
import editIcon from "../../../../../assets/images/edit.svg";
import documentIcon from "../../../../../assets/images/icons/document.svg";
import { AdType, IRollAdsModalCardProps } from "../../../../../libs/models/ad-server";
import { Error } from "../../../../../libs";
import { CembooPlayer } from "@haydenfilms-institute/cemboo-player";

export const RollAdsModalCard: React.FC<IRollAdsModalCardProps> = ({
  uploadedFile,
  handleDeleteFile,
  setUploadedFiles,
  setAdsType,
  handleTypeChange,
  setCount,
  formik
}) => {
  const [isEditing, setIsEditing] = useState<{ [key: number]: boolean }>({});
  const [selectedFileTypes, setSelectedFileTypes] = useState<{ [key: number]: AdType | "" }>({});
  const [originalFileTypes, setOriginalFileTypes] = useState<{ [key: number]: AdType | "" }>({});
  const [showPlayer, setShowPlayer] = useState(false);
  const [adFile, setAdFile] = useState<any>();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    setIsEditing((prev) =>
      uploadedFile.reduce((acc, file, index) => {
        acc[index] = prev[index] ?? !file.adType;
        return acc;
      }, {} as { [key: number]: boolean })
    );

    setSelectedFileTypes((prev) =>
      uploadedFile.reduce((acc, file, index) => {
        acc[index] = prev[index] ?? file.adType ?? "";
        return acc;
      }, {} as { [key: number]: AdType | "" })
    );

    setOriginalFileTypes((prev) =>
      uploadedFile.reduce((acc, file, index) => {
        acc[index] = prev[index] ?? file.adType ?? "";
        return acc;
      }, {} as { [key: number]: AdType | "" })
    );
  }, [uploadedFile]);

  const handleSave = (index: number) => {
    if (!selectedFileTypes[index]) return;
    setCount(true);

    formik.setFieldValue(`videoFiles[${index}].fileType`, selectedFileTypes[index]);
    setIsEditing((prev) => ({ ...prev, [index]: false }));
    setOriginalFileTypes((prev) => ({ ...prev, [index]: selectedFileTypes[index] }));

    setUploadedFiles((prev) => {
      const updatedFiles = [...prev];
      updatedFiles[index].fileType = selectedFileTypes[index];
      return updatedFiles;
    });
  };

  const handleEdit = (index: number) => {
    setIsEditing((prev) => ({ ...prev, [index]: true }));
  };

  const handleDiscard = (index: number) => {
    setCount(true);

    if (!originalFileTypes[index]) {
      // If it's a new file, remove it completely
      handleDeleteFile(index);
    } else {
      // If editing an existing file, revert to original type
      setSelectedFileTypes((prev) => ({ ...prev, [index]: originalFileTypes[index] }));
      setIsEditing((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleDelete = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    formik.setFieldValue("videoFiles", uploadedFile.filter((_, i) => i !== index));

    setIsEditing((prev) => {
      const updatedEditing = { ...prev };
      delete updatedEditing[index];
      return updatedEditing;
    });

    setSelectedFileTypes((prev) => {
      const updatedFileTypes = { ...prev };
      delete updatedFileTypes[index];
      return updatedFileTypes;
    });

    setOriginalFileTypes((prev) => {
      const updatedOriginalFileTypes = { ...prev };
      delete updatedOriginalFileTypes[index];
      return updatedOriginalFileTypes;
    });
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragEnter = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;
    setHoveredIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setHoveredIndex(null);
  };

  const handleDrop = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;

    const reorderedFiles = [...uploadedFile];
    const [movedFile] = reorderedFiles.splice(draggedIndex, 1);
    reorderedFiles.splice(index, 0, movedFile);
    setSelectedFileTypes((prev) => {
      const updated = { ...prev };
      [updated[draggedIndex], updated[index]] = [updated[index], updated[draggedIndex]];

      return updated;
    });

    setUploadedFiles(reorderedFiles);
    setDraggedIndex(null);
    setHoveredIndex(null);
  };

  return (
    <>
      {uploadedFile.map((file, index) =>
        isEditing[index] ? (
          <Col key={index}>
            <Styled.DiscardSaveCard>
              <Styled.DiscardSaveCardInner>
                <Styled.FileInfo>
                  <Styled.FileIcon src={documentIcon} alt="File" />
                  <Styled.FileDetails>
                    <Styled.FileType>File #{index + 1}</Styled.FileType>
                  </Styled.FileDetails>
                </Styled.FileInfo>
                <Styled.FileActions>
                  <Styled.FileName onClick={() => handleDiscard(index)}>Discard</Styled.FileName>
                  <Styled.FileType
                    onClick={() => handleSave(index)}
                    style={{ opacity: selectedFileTypes[index] ? 1 : 0.5, pointerEvents: selectedFileTypes[index] ? "auto" : "none" }}
                  >
                    Save
                  </Styled.FileType>
                </Styled.FileActions>
              </Styled.DiscardSaveCardInner>

              <Styled.DiscardSaveCardInner>
                <Styled.FileInfoContainer>
                  <Styled.FileName>{file?.file ? file.file.name : file.fileName}</Styled.FileName>
                </Styled.FileInfoContainer>

                <Styled.AddTypeSelectContainer>
                  <Select
                    menuShouldScrollIntoView={false}
                    styles={Styled.selectStyles}
                    placeholder="Select Ad Type"
                    value={selectedFileTypes[index] ? { value: selectedFileTypes[index], label: selectedFileTypes[index] } : null}
                    options={Object.values(AdType).map((type) => ({ value: type, label: type }))}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        setSelectedFileTypes((prev) => ({ ...prev, [index]: selectedOption.value as AdType }));
                        setAdsType(selectedOption.value as AdType);
                        handleTypeChange(index, selectedOption.value as AdType);
                      }
                    }}
                  />
                </Styled.AddTypeSelectContainer>
              </Styled.DiscardSaveCardInner>
            </Styled.DiscardSaveCard>
            {formik.touched.videoFiles && formik.errors.videoFiles && <Error message={formik.errors.videoFiles as string} />}
          </Col>
        ) : (
          <Col
            key={index}
            draggable={!isEditing[index]}
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={() => handleDragEnter(index)}
            onDragEnd={handleDragEnd}
            onDrop={() => handleDrop(index)}
          >
            <Styled.FilePreviewCard
              style={{
                opacity: draggedIndex === index ? 0.5 : 1,
                transform: draggedIndex === index ? "scale(1.05)" : hoveredIndex === index ? "scale(1.02)" : "scale(1)",
                transition: "transform 0.2s ease, opacity 0.2s ease",
                border: hoveredIndex === index ? "2px dashed #007bff" : "1px solid #414347",
              }}
            >
              <Styled.FileInfo>
                <Styled.FileIcon src={documentIcon} alt="File" />
                <Styled.FileDetails>
                  <Styled.FileType>{` (${selectedFileTypes[index] ? selectedFileTypes[index].charAt(0).toUpperCase() + selectedFileTypes[index].slice(1).toLowerCase() : ""})`}</Styled.FileType>
                  <Styled.FileName>{file?.file ? file.file.name : file.fileName}</Styled.FileName>
                </Styled.FileDetails>
              </Styled.FileInfo>
              <Styled.FileActions>
                {
                  (file.videoUrl || file.previewUrl) &&
                  <Styled.PlayIcon onClick={() => {
                    setAdFile(file);
                    setShowPlayer(true);
                  }} />
                }
                <Styled.ActionIcon src={deleteIcon} alt="Delete" onClick={() => handleDelete(index)} />
                <Styled.ActionIcon src={editIcon} alt="Edit" onClick={() => handleEdit(index)} />
              </Styled.FileActions>
            </Styled.FilePreviewCard>
          </Col>
        )
      )}
      {
        showPlayer && <Modal
          show={showPlayer}
          fullscreen={true}
          onHide={() => {
            setShowPlayer(false);
            setAdFile("");
          }}
        >
          <Styled.PlayerModalHeader
            closeButton
            closeVariant="white"
          >
            <Modal.Title>
              {adFile?.file?.name || adFile.fileName}
            </Modal.Title>
          </Styled.PlayerModalHeader>
          <Styled.PlayerModalBody>
            <CembooPlayer
              url={adFile.videoUrl || adFile.previewUrl}
              showThumbnail={true}
            />
          </Styled.PlayerModalBody>
        </Modal>
      }
    </>
  );
};